import React, { useEffect, useState } from 'react';
import { Button, Col, Form, message, Popconfirm, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { BookySelectDropDown } from '../../Forms/BookySelectDropDown';
import { OneFrontDatePicker } from '../../Forms/BookyDatePicker';
import { FortnoxInvoiceRowsEditor, sumFortnoxRows } from '../../Forms/FortnoxInvoiceRowsEditor';
import { BookyButton } from '../../Buttons/BookyButton';
import { TextService } from '../../../Services/TextService';
import { IntegrationService } from '../../../Services/IntegrationService';
import { getPreviousCurrentAndNextYearAsOption } from '../../../Helpers/PaymentYearOptions';
import { getPreviousYear } from '../../../Helpers/DateHelpers';
import { DEFAULT_FORTNOX_VAT_RATE, formatKr } from '../../../Helpers/FortnoxInvoiceHelpers';
import { WebApiIntegrationsDtoExternalArticleDto, WebApiMemberDtoExternalPaymentDto } from '../../../Repository/eventbookyapi';
import { IntegrationType } from '../../../Types/IntegrationType';
import { useAuth } from '../../../Contexts/AuthContext';

interface AddFortnoxInvoiceFormProps {
    memberId: number;
    externalPayments: WebApiMemberDtoExternalPaymentDto[];
    onInvoiceCreated: () => void;
    onCancel: () => void;
}

const AddFortnoxInvoiceForm = ({ memberId, onInvoiceCreated, onCancel, externalPayments }: AddFortnoxInvoiceFormProps) => {
    const [form] = Form.useForm();
    const GetText = TextService.GetText;
    const { user } = useAuth();
    const [isCreating, setIsCreating] = useState(false);
    const [articles, setArticles] = useState<WebApiIntegrationsDtoExternalArticleDto[]>([]);
    const [memberInFortnox, setMemberInFortnox] = useState(true);
    const selectedYear = Form.useWatch('year', form);
    const watchedRows = Form.useWatch('rows', form);
    const watchedInvoiceDate = Form.useWatch('invoiceDate', form);

    useEffect(() => {
        IntegrationService.GetExternalArticles(IntegrationType.Fortnox)
            .then(setArticles)
            .catch(() => setArticles([]));

        IntegrationService.GetFortnoxLinkedMembers()
            .then(ids => setMemberInFortnox(ids.includes(memberId)))
            .catch(() => setMemberInFortnox(true));
    }, [memberId]);

    // Due date can't be before the invoice date (or today, if no invoice date is set).
    const disabledDueDate = (current: dayjs.Dayjs) => {
        if (!current) return false;
        const floor = watchedInvoiceDate ? dayjs(watchedInvoiceDate).startOf('day') : dayjs().startOf('day');
        return current.isBefore(floor);
    };

    const handleCreate = async () => {
        try {
            await form.validateFields();
        } catch {
            return;
        }
        try {
            setIsCreating(true);
            const dueDate = form.getFieldValue('dueDate');
            const invoiceDate = form.getFieldValue('invoiceDate');
            const rows = (form.getFieldValue('rows') as any[]).map(r => ({
                description: (r.description ?? '').toString(),
                amount: Number(r.amount) || 0,
                vat: r.vat ?? DEFAULT_FORTNOX_VAT_RATE,
                articleNumber: r.articleNumber || undefined,
            }));
            await IntegrationService.CreateFortnoxInvoice({
                memberId: memberId,
                // description/amount kept for the DTO's legacy single-row shape; rows is what's used.
                description: rows[0]?.description ?? '',
                amount: rows[0]?.amount ?? 0,
                connectedYear: form.getFieldValue('year').toString(),
                // new Date(YYYY-MM-DD) parses as UTC midnight - avoids the date shifting a day when
                // the generated client serializes it back with toISOString().
                dueDate: dueDate ? new Date(dayjs(dueDate).format('YYYY-MM-DD')) : undefined,
                invoiceDate: invoiceDate ? new Date(dayjs(invoiceDate).format('YYYY-MM-DD')) : undefined,
                rows,
            });
            message.success(GetText("ManagePayments_FortnoxInvoiceCreated"));
            form.resetFields();
            onInvoiceCreated();
        } catch {
            message.warning(GetText("EditMember_ErrorSaving"));
        } finally {
            setIsCreating(false);
        }
    };

    const hasActiveInvoice = externalPayments.some(ep =>
        ep.connectedYear === (selectedYear ?? form.getFieldValue("year"))?.toString() &&
        ep.integrationType === IntegrationType.Fortnox
    );

    const breakdown = sumFortnoxRows(watchedRows);

    return (
        <div style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0 }}>{GetText("ManagePayments_AddFortnoxInvoiceTitle")}</h4>
                <Button type="link" size="small" onClick={onCancel}>
                    {GetText("AddPaymentLink_CollapseForm")}
                </Button>
            </div>
            <Form form={form} colon={false} labelAlign="left" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                initialValues={{ year: getPreviousYear(0), rows: [{ vat: DEFAULT_FORTNOX_VAT_RATE }] }}
            >
                <Col span={20}>
                    <div style={{ color: '#666', fontSize: 13, marginBottom: 20, maxWidth: 480 }}>
                        {GetText("FortnoxInvoice_DraftOnlyNote")}
                    </div>
                    <FortnoxInvoiceRowsEditor form={form} articles={articles} />
                    <div style={{ color: '#666', fontSize: 13, margin: '0 0 12px' }}>
                        {GetText("FortnoxInvoice_SummaryNet")}: {formatKr(breakdown.net)} &nbsp;·&nbsp;
                        {GetText("FortnoxInvoice_SummaryVat")}: {formatKr(breakdown.vat)} &nbsp;·&nbsp;
                        <strong>{GetText("FortnoxInvoice_SummaryGross")}: {formatKr(breakdown.gross)}</strong>
                    </div>
                    <BookySelectDropDown label={GetText("FortnoxInvoice_MembershipYearLabel")} name="year" options={getPreviousCurrentAndNextYearAsOption()} handleChange={() => { }} help={GetText("FortnoxInvoice_MembershipYearTooltip")} helpTitle={GetText("FortnoxInvoice_MembershipYearLabel")} />
                    <OneFrontDatePicker label={GetText("FortnoxInvoice_InvoiceDateLabel")} name="invoiceDate" placeholder={GetText("FortnoxInvoice_InvoiceDatePlaceholder")} tooltip={GetText("FortnoxInvoice_InvoiceDateTooltip")} />
                    <OneFrontDatePicker label={GetText("FortnoxInvoice_DueDateLabel")} name="dueDate" placeholder={GetText("FortnoxInvoice_DueDatePlaceholder")} disabledDate={disabledDueDate} />
                    {!memberInFortnox && (
                        <div style={{ color: '#d48806', fontSize: 13, marginBottom: 8, maxWidth: 480 }}>
                            {GetText("FortnoxInvoice_MemberNotInFortnoxNote")}
                        </div>
                    )}
                    <Form.Item label={" "}>
                        <Tooltip title={hasActiveInvoice ? GetText("ManagePayments_ActiveFortnoxInvoiceExists") + " " + form.getFieldValue("year")?.toString() : undefined}>
                            <span style={hasActiveInvoice ? { display: 'inline-block', cursor: 'not-allowed' } : undefined}>
                                {memberInFortnox ? (
                                    <BookyButton type="primary" disabled={hasActiveInvoice || user?.hasOnlyReadRights} fullWidth={false} text={GetText("ManagePayments_CreateFortnoxInvoiceButton")} onClick={handleCreate} loading={isCreating} />
                                ) : (
                                    <Popconfirm
                                        title={GetText("FortnoxInvoice_CreateCustomerConfirmTitle")}
                                        description={GetText("FortnoxInvoice_CreateCustomerConfirmDesc")}
                                        okText={GetText("Common_Yes")}
                                        cancelText={GetText("Common_No")}
                                        onConfirm={handleCreate}
                                        disabled={hasActiveInvoice || user?.hasOnlyReadRights}
                                    >
                                        <BookyButton type="primary" disabled={hasActiveInvoice || user?.hasOnlyReadRights} fullWidth={false} text={GetText("ManagePayments_CreateFortnoxInvoiceButton")} loading={isCreating} />
                                    </Popconfirm>
                                )}
                            </span>
                        </Tooltip>
                    </Form.Item>
                </Col>
            </Form>
        </div>
    );
};

export default AddFortnoxInvoiceForm;
